import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, LogOut, Loader, Calendar, Clock, Users, UserPlus, Key, Download, UploadCloud, Database, Wand2 } from 'lucide-react';
import { API_ENDPOINTS, authToken, isBackendAvailable, setUsingFallback } from '../utils/api';
import { dbAuth, dbImages, dbBackup, type User as DbUser, type GalleryImage } from '../utils/dbBackend';
import { BookingManager } from './BookingManager';
import { BusinessHoursSettings } from './BusinessHoursSettings';
import { ImageSEOAssistant } from './ImageSEOAssistant';

// Re-export type for backwards compatibility
type AdminUser = DbUser;

/** Resize image to max dimensions, returns JPEG blob. */
function resizeImage(
  file: File,
  maxWidth = 1200,
  maxHeight = 900,
  quality = 0.85
): Promise<File> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { width, height } = img;

      // Skip resize if already within bounds
      if (width <= maxWidth && height <= maxHeight) {
        resolve(file);
        return;
      }

      // Scale down maintaining aspect ratio
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(file);
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const name = file.name.replace(/\.[^.]+$/, ".jpg");
          resolve(new File([blob], name, { type: "image/jpeg" }));
        },
        "image/jpeg",
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };

    img.src = url;
  });
}

interface DisplayImage {
  id: string;
  imageUrl: string | null;
  uploadedAt: string;
}

export function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<DbUser | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [images, setImages] = useState<DisplayImage[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'images' | 'bookings' | 'hours' | 'users' | 'seo'>('images');
  const [useLocalAuth, setUseLocalAuth] = useState(false);
  
  // User management state
  const [users, setUsers] = useState<DbUser[]>([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<'admin' | 'user'>('user');
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Backup/restore state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [backupLoading, setBackupLoading] = useState(false);

  useEffect(() => {
    checkBackendAndSession();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchImages();
      fetchUsers();
    }
  }, [isAuthenticated]);

  const checkBackendAndSession = async () => {
    // First check if backend is available
    const backendAvailable = await isBackendAvailable();
    
    if (!backendAvailable) {
      setUseLocalAuth(true);
      setUsingFallback(true);
      
      // Check for existing local token
      const token = dbAuth.getToken();
      if (token) {
        const isValid = await dbAuth.verify(token);
        if (isValid) {
          setAccessToken(token);
          setCurrentUser(dbAuth.getCurrentUser());
          setIsAuthenticated(true);
        }
      }
      return;
    }
    
    // Backend is available, check session normally
    const token = authToken.get();
    if (token) {
      try {
        const response = await fetch(API_ENDPOINTS.verify, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (response.ok) {
          setAccessToken(token);
          setIsAuthenticated(true);
        } else {
          authToken.remove();
        }
      } catch (error) {
        authToken.remove();
      }
    }
  };

  const fetchUsers = async () => {
    try {
      if (useLocalAuth) {
        // Use IndexedDB
        const allUsers = await dbAuth.getAllUsers();
        setUsers(allUsers);
      } else {
        // Use backend API
        const response = await fetch(API_ENDPOINTS.users, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        const data = await response.json();
        if (data.users) {
          setUsers(data.users);
        }
      }
    } catch (error: any) {
      // Failed to fetch users
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    
    try {
      if (useLocalAuth) {
        await dbAuth.addUser(newUserName, newUserEmail, newUserPassword, newUserRole);
      } else {
        // Use backend API
        const response = await fetch(API_ENDPOINTS.users, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            username: newUserName,
            email: newUserEmail,
            password: newUserPassword,
            role: newUserRole,
          }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to add user');
        }
      }
      setMessage('User added successfully!');
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRole('user');
      setShowAddUser(false);
      fetchUsers();
    } catch (error: any) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string, userEmail?: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    
    setLoading(true);
    setMessage('');
    
    try {
      if (useLocalAuth) {
        await dbAuth.deleteUser(userId);
      } else {
        // Use backend API - delete by email
        if (!userEmail) {
          throw new Error('Email required for deletion');
        }
        const response = await fetch(API_ENDPOINTS.deleteUser(userEmail), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to delete user');
        }
      }
      setMessage('User deleted successfully!');
      fetchUsers();
    } catch (error: any) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    
    if (newPassword !== confirmPassword) {
      setMessage('Error: New passwords do not match');
      setLoading(false);
      return;
    }
    
    if (newPassword.length < 6) {
      setMessage('Error: Password must be at least 6 characters');
      setLoading(false);
      return;
    }
    
    try {
      if (useLocalAuth) {
        // Use IndexedDB
        if (!currentUser) {
          throw new Error('No user logged in');
        }
        await dbAuth.changePassword(currentUser.id, oldPassword, newPassword);
      } else {
        // Use backend API
        const response = await fetch(API_ENDPOINTS.changePassword, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            oldPassword,
            newPassword,
          }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to change password');
        }
      }
      setMessage('Password changed successfully!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowChangePassword(false);
    } catch (error: any) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      if (useLocalAuth) {
        // Use IndexedDB (Dexie) authentication
        const token = await dbAuth.login(email, password);
        setAccessToken(token);
        setCurrentUser(dbAuth.getCurrentUser());
        setIsAuthenticated(true);
        setMessage('Login successful!');
      } else {
        // Use Netlify Functions backend
        const response = await fetch(API_ENDPOINTS.login, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });

        const data = await response.json();

        if (response.ok && data.session?.access_token) {
          const token = data.session.access_token;
          authToken.set(token);
          setAccessToken(token);
          // Set currentUser from backend response
          if (data.user) {
            setCurrentUser({
              id: data.user.id || 'unknown',
              email: data.user.email,
              username: data.user.username || data.user.name,
              passwordHash: '', // Not needed on frontend
              role: data.user.role || 'user',
              createdAt: new Date().toISOString(),
            });
          }
          setIsAuthenticated(true);
          setMessage('Login successful!');
        } else {
          setMessage(data.error || 'Login failed');
        }
      }
    } catch (error: any) {
      setMessage(`Login error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    if (useLocalAuth) {
      await dbAuth.logout();
    } else {
      authToken.remove();
    }
    setIsAuthenticated(false);
    setAccessToken(null);
    setCurrentUser(null);
    setEmail('');
    setPassword('');
  };

  const fetchImages = async () => {
    try {
      if (useLocalAuth) {
        // Use IndexedDB (Dexie)
        const dbData = await dbImages.getAll();
        // Convert to display format with object URLs for blobs
        const displayImages: DisplayImage[] = dbData.map((img: GalleryImage) => ({
          id: img.id,
          imageUrl: dbImages.getImageUrl(img),
          uploadedAt: img.uploadedAt,
        }));
        setImages(displayImages);
      } else {
        const response = await fetch(API_ENDPOINTS.images);
        const data = await response.json();

        if (data.images) {
          setImages(data.images);
        }
      }
    } catch (error: any) {
      setMessage(`Failed to fetch images: ${error.message}`);
    }
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setImageFiles(files);
      setImagePreviews(files.map(f => URL.createObjectURL(f)));
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (imageFiles.length === 0) {
      setMessage('Please select at least one image');
      return;
    }

    setLoading(true);
    setMessage('');

    const total = imageFiles.length;
    let uploaded = 0;
    let failed = 0;

    try {
      for (const file of imageFiles) {
        try {
          // Resize image before upload (max 1200x900, JPEG 85%)
          const resized = await resizeImage(file);

          if (useLocalAuth) {
            await dbImages.add(resized);
            uploaded++;
          } else {
            const formData = new FormData();
            formData.append('image', resized);

            const response = await fetch(API_ENDPOINTS.uploadImage, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${accessToken}`,
              },
              body: formData,
            });

            const data = await response.json();

            if (response.ok && !data.error) {
              uploaded++;
            } else {
              failed++;
            }
          }
          setMessage(`Uploading ${uploaded + failed}/${total}...`);
        } catch {
          failed++;
        }
      }

      if (failed === 0) {
        setMessage(`${uploaded} image${uploaded > 1 ? 's' : ''} uploaded successfully!`);
      } else {
        setMessage(`${uploaded} uploaded, ${failed} failed`);
      }
      setImageFiles([]);
      setImagePreviews([]);
      fetchImages();
    } catch (error: any) {
      setMessage(`Upload error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (imageId: string) => {
    if (!confirm('Are you sure you want to delete this image?')) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      if (useLocalAuth) {
        await dbImages.delete(imageId);
        setMessage('Image deleted successfully!');
        fetchImages();
      } else {
        const response = await fetch(API_ENDPOINTS.deleteImage(imageId), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        const data = await response.json();
        
        if (response.ok) {
          setMessage('Image deleted successfully!');
          fetchImages();
        } else {
          setMessage(data.error || 'Delete failed');
        }
      }
    } catch (error: any) {
      setMessage(`Delete error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Backup/Restore handlers
  const handleExportBackup = async () => {
    setBackupLoading(true);
    try {
      await dbBackup.downloadBackup();
      setMessage('Backup downloaded successfully!');
    } catch (error: any) {
      setMessage(`Backup error: ${error.message}`);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleImportBackup = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!confirm('This will replace ALL current data with the backup. Are you sure?')) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setBackupLoading(true);
    try {
      await dbBackup.import(file);
      setMessage('Backup restored successfully! Refreshing...');
      // Refresh data
      await fetchUsers();
      await fetchImages();
      // Re-check auth since it may have changed
      await checkBackendAndSession();
    } catch (error: any) {
      setMessage(`Restore error: ${error.message}`);
    } finally {
      setBackupLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (!isAuthenticated) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: '#E8B8B0' }}
      >
        <div
          className="w-full max-w-md p-8 rounded-3xl shadow-lg"
          style={{ backgroundColor: '#F0C2B5' }}
        >
          <h2 className="mb-6 text-center" style={{ color: '#444444' }}>
            ShowGroom Admin
          </h2>
          {useLocalAuth && (
            <div className="mb-4 text-center text-sm" style={{ color: '#444444' }}>
              <p>📦 Running in offline mode</p>
            </div>
          )}
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block mb-2" style={{ color: '#444444' }}>
                Email
              </label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                className="w-full px-4 py-2 rounded-lg border-none outline-none"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
              />
            </div>
            
            <div>
              <label className="block mb-2" style={{ color: '#444444' }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                className="w-full px-4 py-2 rounded-lg border-none outline-none"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
              />
            </div>
            
            {message && (
              <div
                className="p-3 rounded-lg text-center"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
              >
                {message}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
              style={{ backgroundColor: '#F29B70', color: '#444444' }}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen p-4"
      style={{ backgroundColor: '#E8B8B0' }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                window.history.pushState({}, '', '/');
                window.location.href = '/';
              }}
              className="px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all"
              style={{ backgroundColor: '#F0C2B5', color: '#444444' }}
            >
              ← Back to Site
            </button>
            <h1 style={{ color: '#444444' }}>ShowGroom Admin Panel</h1>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all"
            style={{ backgroundColor: '#F29B70', color: '#444444' }}
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setActiveTab('images')}
            className={`px-6 py-3 rounded-full shadow-md transition-all ${
              activeTab === 'images' ? 'shadow-lg' : 'opacity-70 hover:opacity-100'
            }`}
            style={{
              backgroundColor: activeTab === 'images' ? '#F29B70' : '#F0C2B5',
              color: '#444444',
            }}
          >
            <Upload size={18} className="inline mr-2" />
            Gallery Images
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-6 py-3 rounded-full shadow-md transition-all ${
              activeTab === 'bookings' ? 'shadow-lg' : 'opacity-70 hover:opacity-100'
            }`}
            style={{
              backgroundColor: activeTab === 'bookings' ? '#F29B70' : '#F0C2B5',
              color: '#444444',
            }}
          >
            <Calendar size={18} className="inline mr-2" />
            Bookings
          </button>
          <button
            onClick={() => setActiveTab('hours')}
            className={`px-6 py-3 rounded-full shadow-md transition-all ${
              activeTab === 'hours' ? 'shadow-lg' : 'opacity-70 hover:opacity-100'
            }`}
            style={{
              backgroundColor: activeTab === 'hours' ? '#F29B70' : '#F0C2B5',
              color: '#444444',
            }}
          >
            <Clock size={18} className="inline mr-2" />
            Business Hours
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 rounded-full shadow-md transition-all ${
              activeTab === 'users' ? 'shadow-lg' : 'opacity-70 hover:opacity-100'
            }`}
            style={{
              backgroundColor: activeTab === 'users' ? '#F29B70' : '#F0C2B5',
              color: '#444444',
            }}
          >
            <Users size={18} className="inline mr-2" />
            Users
          </button>
          <button
            onClick={() => setActiveTab('seo')}
            className={`px-6 py-3 rounded-full shadow-md transition-all ${
              activeTab === 'seo' ? 'shadow-lg' : 'opacity-70 hover:opacity-100'
            }`}
            style={{
              backgroundColor: activeTab === 'seo' ? '#F29B70' : '#F0C2B5',
              color: '#444444',
            }}
          >
            <Wand2 size={18} className="inline mr-2" />
            SEO Assistant
          </button>
        </div>

        {activeTab === 'users' ? (
          /* Users Management */
          <div
            className="p-6 rounded-3xl shadow-lg"
            style={{ backgroundColor: '#F0C2B5' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 style={{ color: '#444444' }}>User Management</h2>
              <button
                onClick={() => setShowAddUser(!showAddUser)}
                className="flex items-center gap-2 px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all"
                style={{ backgroundColor: '#F29B70', color: '#444444' }}
              >
                <UserPlus size={18} />
                Add User
              </button>
            </div>

            {/* Add User Form */}
            {showAddUser && (
              <div
                className="mb-6 p-4 rounded-xl"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
              >
                <h3 className="mb-4" style={{ color: '#444444' }}>Add New User</h3>
                <form onSubmit={handleAddUser} className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block mb-1 text-sm" style={{ color: '#444444' }}>
                        Username
                      </label>
                      <input
                        type="text"
                        value={newUserName}
                        onChange={(e) => setNewUserName(e.target.value)}
                        required
                        className="w-full px-3 py-2 rounded-lg border-none outline-none"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm" style={{ color: '#444444' }}>
                        Email
                      </label>
                      <input
                        type="email"
                        value={newUserEmail}
                        onChange={(e) => setNewUserEmail(e.target.value)}
                        required
                        className="w-full px-3 py-2 rounded-lg border-none outline-none"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm" style={{ color: '#444444' }}>
                        Password
                      </label>
                      <input
                        type="password"
                        value={newUserPassword}
                        onChange={(e) => setNewUserPassword(e.target.value)}
                        required
                        className="w-full px-3 py-2 rounded-lg border-none outline-none"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm" style={{ color: '#444444' }}>
                        Role
                      </label>
                      <select
                        value={newUserRole}
                        onChange={(e) => setNewUserRole(e.target.value as 'admin' | 'user')}
                        className="w-full px-3 py-2 rounded-lg border-none outline-none"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                      style={{ backgroundColor: '#F29B70', color: '#444444' }}
                    >
                      {loading ? 'Adding...' : 'Add User'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddUser(false)}
                      className="px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {message && (
              <div
                className="mb-4 p-3 rounded-lg text-center"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
              >
                {message}
              </div>
            )}

            {/* Users List */}
            <div className="space-y-3">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 rounded-xl"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span style={{ color: '#444444', fontWeight: 500 }}>{user.username}</span>
                      <span
                        className="px-2 py-0.5 rounded-full text-sm"
                        style={{
                          backgroundColor: user.role === 'admin' ? '#F29B70' : 'rgba(255, 255, 255, 0.7)',
                          color: '#444444'
                        }}
                      >
                        {user.role}
                      </span>
                      {user.id === currentUser?.id && (
                        <span className="text-sm" style={{ color: '#444444' }}>(you)</span>
                      )}
                    </div>
                    <p className="text-sm" style={{ color: '#444444' }}>{user.email}</p>
                  </div>
                  <div className="flex gap-2">
                    {user.id === currentUser?.id && (
                      <button
                        onClick={() => setShowChangePassword(true)}
                        disabled={loading}
                        className="p-2 rounded-lg hover:shadow-md transition-all disabled:opacity-50"
                        style={{ backgroundColor: '#F29B70', color: '#444444' }}
                        title="Change password"
                      >
                        <Key size={18} />
                      </button>
                    )}
                    {user.id !== currentUser?.id && user.id !== 'default-admin' && (
                      <button
                        onClick={() => handleDeleteUser(user.id, user.email)}
                        disabled={loading}
                        className="p-2 rounded-lg hover:shadow-md transition-all disabled:opacity-50"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
                        title="Delete user"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Backup/Restore Section */}
            <div
              className="mt-6 p-4 rounded-xl"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
            >
              <h3 className="mb-4 flex items-center gap-2" style={{ color: '#444444' }}>
                <Database size={20} />
                Database Backup & Restore
              </h3>
              <p className="mb-4 text-sm" style={{ color: '#444444' }}>
                Export your data to a backup file or restore from a previous backup.
                Backups include all users, bookings, images, and settings.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleExportBackup}
                  disabled={backupLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                  style={{ backgroundColor: '#F29B70', color: '#444444' }}
                >
                  <Download size={18} />
                  {backupLoading ? 'Exporting...' : 'Export Backup'}
                </button>
                <label
                  className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all cursor-pointer ${backupLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
                >
                  <UploadCloud size={18} />
                  {backupLoading ? 'Importing...' : 'Import Backup'}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleImportBackup}
                    disabled={backupLoading}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Change Password Modal */}
            {showChangePassword && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div
                  className="w-full max-w-md p-6 rounded-3xl shadow-lg"
                  style={{ backgroundColor: '#F0C2B5' }}
                >
                  <h3 className="mb-4" style={{ color: '#444444' }}>Change Password</h3>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <label className="block mb-1 text-sm" style={{ color: '#444444' }}>
                        Current Password
                      </label>
                      <input
                        type="password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        required
                        className="w-full px-3 py-2 rounded-lg border-none outline-none"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
                        placeholder="Enter current password"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm" style={{ color: '#444444' }}>
                        New Password
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                        className="w-full px-3 py-2 rounded-lg border-none outline-none"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
                        placeholder="Enter new password (min 6 characters)"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm" style={{ color: '#444444' }}>
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={6}
                        className="w-full px-3 py-2 rounded-lg border-none outline-none"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
                        placeholder="Confirm new password"
                      />
                    </div>
                    {message && (
                      <div
                        className="p-3 rounded-lg text-center"
                        style={{
                          backgroundColor: message.includes('Error') ? 'rgba(255, 100, 100, 0.3)' : 'rgba(255, 255, 255, 0.7)',
                          color: '#444444'
                        }}
                      >
                        {message}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                        style={{ backgroundColor: '#F29B70', color: '#444444' }}
                      >
                        {loading ? 'Changing...' : 'Change Password'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowChangePassword(false);
                          setOldPassword('');
                          setNewPassword('');
                          setConfirmPassword('');
                          setMessage('');
                        }}
                        className="flex-1 px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'seo' ? (
          /* SEO Assistant */
          <div
            className="p-6 rounded-3xl shadow-lg"
            style={{ backgroundColor: '#F0C2B5' }}
          >
            <ImageSEOAssistant />
          </div>
        ) : activeTab === 'bookings' ? (
          /* Bookings Manager */
          <div
            className="p-6 rounded-3xl shadow-lg"
            style={{ backgroundColor: '#F0C2B5' }}
          >
            <BookingManager accessToken={accessToken!} language="en" />
          </div>
        ) : activeTab === 'hours' ? (
          /* Business Hours Settings */
          <div
            className="p-6 rounded-3xl shadow-lg"
            style={{ backgroundColor: '#F0C2B5' }}
          >
            <BusinessHoursSettings accessToken={accessToken!} />
          </div>
        ) : (
          <>
            {/* Upload Form */}
            <div
              className="p-6 rounded-3xl shadow-lg mb-6"
              style={{ backgroundColor: '#F0C2B5' }}
            >
          <h2 className="mb-4" style={{ color: '#444444' }}>
            Upload Image
          </h2>

          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageFileChange}
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className="block w-full max-w-md mx-auto rounded-xl cursor-pointer border-2 border-dashed flex items-center justify-center overflow-hidden"
                style={{ borderColor: '#F29B70', backgroundColor: 'rgba(255, 255, 255, 0.5)', minHeight: '200px' }}
              >
                {imagePreviews.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2 p-3 w-full">
                    {imagePreviews.map((preview, i) => (
                      <img key={i} src={preview} alt={`Preview ${i + 1}`} className="w-full aspect-square object-cover rounded-lg" />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Upload size={32} style={{ color: '#F29B70', margin: '0 auto' }} />
                    <p style={{ color: '#444444' }} className="mt-2">Click to select images</p>
                    <p style={{ color: '#999' }} className="text-sm mt-1">You can select multiple files</p>
                  </div>
                )}
              </label>
            </div>

            {message && (
              <div
                className="p-3 rounded-lg text-center"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', color: '#444444' }}
              >
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || imageFiles.length === 0}
              className="w-full px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#F29B70', color: '#444444' }}
            >
              {loading ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={18} />
                  Upload {imageFiles.length > 1 ? `${imageFiles.length} Images` : 'Image'}
                </>
              )}
            </button>
          </form>
        </div>

        {/* Image Gallery */}
        <div
          className="p-6 rounded-3xl shadow-lg"
          style={{ backgroundColor: '#F0C2B5' }}
        >
          <h2 className="mb-4" style={{ color: '#444444' }}>
            Uploaded Images ({images.length})
          </h2>

          {images.length === 0 ? (
            <p style={{ color: '#444444' }} className="text-center py-8">
              No images uploaded yet
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image) => (
                <div
                  key={image.id}
                  className="rounded-xl overflow-hidden"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                >
                  {image.imageUrl ? (
                    <img src={image.imageUrl} alt="Gallery" className="w-full aspect-square object-cover" />
                  ) : (
                    <div className="w-full aspect-square bg-gray-200 flex items-center justify-center">
                      <span style={{ color: '#444444' }}>No image</span>
                    </div>
                  )}
                  <div className="p-3">
                    <button
                      onClick={() => handleDelete(image.id)}
                      disabled={loading}
                      className="w-full px-4 py-2 rounded-lg flex items-center justify-center gap-2 hover:shadow-md transition-all disabled:opacity-50"
                      style={{ backgroundColor: '#F29B70', color: '#444444' }}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
}
